/**
 * Minimal markdown renderer for Pass guides.
 * Supports: # h1, ## h2, ### h3, **bold**, *italic*, [text](url),
 * unordered lists (-, *), ordered lists (1.), blank-line paragraphs, line breaks.
 *
 * Escapes raw HTML so user-supplied markdown cannot inject scripts.
 * Returns an HTML string suitable for `dangerouslySetInnerHTML`.
 */

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inline(text) {
  let html = escapeHtml(text);
  // [text](url)
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, t, u) => `<a href="${u}" target="_blank" rel="noopener noreferrer" class="text-teal-600 underline">${t}</a>`);
  // **bold**
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // *italic*
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  return html;
}

export function renderMarkdown(md) {
  if (!md) return '';
  const lines = String(md).replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let listType = null; // 'ul' | 'ol'
  let paragraph = [];

  function flushPara() {
    if (paragraph.length) {
      out.push(`<p class="mb-3 text-slate-700 leading-relaxed">${inline(paragraph.join(' '))}</p>`);
      paragraph = [];
    }
  }
  function closeList() {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  }

  for (const raw of lines) {
    const line = raw.trim();

    if (!line) {
      flushPara();
      closeList();
      continue;
    }

    // Headings
    let m = /^(#{1,3})\s+(.*)$/.exec(line);
    if (m) {
      flushPara();
      closeList();
      const level = m[1].length;
      const sizes = { 1: 'text-2xl font-bold text-slate-900 mt-5 mb-2',
                      2: 'text-xl font-bold text-slate-900 mt-4 mb-2',
                      3: 'text-lg font-semibold text-slate-900 mt-3 mb-1.5' };
      out.push(`<h${level} class="${sizes[level]}">${inline(m[2])}</h${level}>`);
      continue;
    }

    // Unordered list
    m = /^[-*]\s+(.*)$/.exec(line);
    if (m) {
      flushPara();
      if (listType !== 'ul') {
        closeList();
        listType = 'ul';
        out.push('<ul class="list-disc pl-6 mb-3 text-slate-700 space-y-1">');
      }
      out.push(`<li>${inline(m[1])}</li>`);
      continue;
    }

    // Ordered list
    m = /^\d+\.\s+(.*)$/.exec(line);
    if (m) {
      flushPara();
      if (listType !== 'ol') {
        closeList();
        listType = 'ol';
        out.push('<ol class="list-decimal pl-6 mb-3 text-slate-700 space-y-1">');
      }
      out.push(`<li>${inline(m[1])}</li>`);
      continue;
    }

    // Paragraph accumulation
    closeList();
    paragraph.push(line);
  }
  flushPara();
  closeList();
  return out.join('\n');
}
